import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  User,
  LoginCredentials,
  LoginResponse,
  RegisterCredentials,
  RegisterResponse,
  RefreshUserOutcome,
  ImpersonationState,
  AuthMeResponse,
} from '@/types/auth'
import apiService from '@/services/api'

export const useAuthStore = defineStore('auth', () => {
  /** Single-flight: concurrent refreshUser() callers share one in-flight GET /auth/me. */
  let refreshUserInFlight: Promise<RefreshUserOutcome> | null = null

  // State
  const user = ref<User | null>(null)
  const token = ref<string | null>(null)
  const impersonation = ref<ImpersonationState>({ active: false })
  /** Suppresses usePermissionGuard while start/stop impersonation applies auth then navigates. */
  const impersonationTransitionInProgress = ref(false)
  const isLoading = ref(false)
  /** True after GET /auth/me failed for non-auth reasons (e.g. deploy blip); cleared on successful refresh. */
  const sessionRefreshDegraded = ref(false)

  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isImpersonating = computed(() => impersonation.value.active === true)

  const applyAuthMePayload = (payload: Pick<AuthMeResponse, 'user' | 'impersonation'>) => {
    user.value = payload.user
    if (payload.user != null) {
      localStorage.setItem('auth_user', JSON.stringify(payload.user))
    } else {
      localStorage.removeItem('auth_user')
    }

    impersonation.value = payload.impersonation ?? { active: false }
  }

  // Initialize from localStorage
  const initAuth = () => {
    const storedToken = localStorage.getItem('auth_token')
    const storedUser = localStorage.getItem('auth_user')

    if (storedToken && storedUser) {
      if (storedUser === 'undefined' || storedUser === '') {
        clearAuth()
        return
      }
      token.value = storedToken
      try {
        user.value = JSON.parse(storedUser)
      } catch (e) {
        console.error('Failed to parse stored user:', e)
        clearAuth()
      }
    }
  }

  // Actions
  const login = async (credentials: LoginCredentials): Promise<LoginResponse> => {
    isLoading.value = true
    try {
      const payload = { ...credentials }
      const storedToken = localStorage.getItem('trusted_device_token')
      if (storedToken) payload.trusted_device_token = storedToken
      const response = await apiService.post<LoginResponse>('/auth/login', payload)

      // If 2FA is required, don't store token yet
      if (response.requires_2fa) {
        return response
      }
      
      // Store token and user (only persist user if present to avoid storing "undefined")
      token.value = response.token ?? null
      user.value = response.user
      if (response.token) {
        localStorage.setItem('auth_token', response.token)
      }
      if (response.user != null) {
        localStorage.setItem('auth_user', JSON.stringify(response.user))
      } else {
        localStorage.removeItem('auth_user')
      }
      
      return response
    } catch (error) {
      clearAuth()
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const register = async (credentials: RegisterCredentials): Promise<RegisterResponse> => {
    isLoading.value = true
    try {
      const response = await apiService.post<RegisterResponse>('/auth/register', credentials)
      
      // Store token and user (same as login - auto-authenticate)
      token.value = response.token
      user.value = response.user
      
      // Persist to localStorage
      localStorage.setItem('auth_token', response.token)
      if (response.user != null) {
        localStorage.setItem('auth_user', JSON.stringify(response.user))
      } else {
        localStorage.removeItem('auth_user')
      }
      
      return response
    } catch (error) {
      clearAuth()
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = async () => {
    isLoading.value = true
    try {
      // Call logout endpoint if authenticated
      if (token.value) {
        await apiService.post('/auth/logout')
      }
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearAuth()
      isLoading.value = false
    }
  }

  const clearAuth = () => {
    user.value = null
    token.value = null
    impersonation.value = { active: false }
    sessionRefreshDegraded.value = false
    hideAppSidebarForTwoFactorSettings.value = false
    localStorage.removeItem('auth_token')
    localStorage.removeItem('auth_user')
    localStorage.removeItem('two_factor_token') // Clear pending 2FA token
    
    // Clear timezone modal session flags on logout
    sessionStorage.removeItem('timezone_modal_later_until')
    
    // Clear trusted device cookie by setting it to expire
    document.cookie = 'trusted_device_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict'
  }

  const refreshUser = async (): Promise<RefreshUserOutcome> => {
    if (!token.value) {
      return { ok: false, reason: 'client' }
    }

    if (refreshUserInFlight) {
      return refreshUserInFlight
    }

    refreshUserInFlight = (async (): Promise<RefreshUserOutcome> => {
      try {
        // Longer than default so /auth/me can wait behind other same-origin requests (browser connection cap).
        const axiosInstance = (apiService as any).api
        const response = await axiosInstance.get('/auth/me', {
          timeout: 90000,
        })

        user.value = response.data.user
        if (response.data.user != null) {
          localStorage.setItem('auth_user', JSON.stringify(response.data.user))
        } else {
          localStorage.removeItem('auth_user')
        }
        impersonation.value = response.data.impersonation ?? { active: false }
        sessionRefreshDegraded.value = false
        return { ok: true, user: response.data.user }
      } catch (error: any) {
        const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout')
        if (isTimeout) {
          console.warn('Auth refresh timed out, but keeping session alive')
          sessionRefreshDegraded.value = true
          return { ok: false, reason: 'timeout' }
        }

        console.error('Failed to refresh user:', error)

        if (error.response?.status === 401) {
          // Hard logout (clear storage + Pinia) is performed by api interceptor for GET /auth/me
          sessionRefreshDegraded.value = false
          return { ok: false, reason: 'unauthorized' }
        }

        if (!error.response) {
          sessionRefreshDegraded.value = true
          return { ok: false, reason: 'network' }
        }

        const st = error.response.status as number
        if (st >= 500) {
          sessionRefreshDegraded.value = true
          return { ok: false, reason: 'server' }
        }

        sessionRefreshDegraded.value = true
        return { ok: false, reason: 'client' }
      }
    })()

    try {
      return await refreshUserInFlight
    } finally {
      refreshUserInFlight = null
    }
  }

  // Permission helpers
  /**
   * Check if user has a specific permission.
   * Super.admin permission (canonical) or super-admin role (legacy alias) bypasses all checks.
   * Matches backend Gate::before — every environment, no env qualifier.
   */
  const hasPermission = (permission: string): boolean => {
    if (!user.value) return false

    const isSuperAdmin =
      user.value.roles.some((r) => {
        const roleName = typeof r === 'string' ? r : r.name
        return roleName === 'super-admin'
      }) ||
      user.value.permissions.some((p) => {
        const permName = typeof p === 'string' ? p : p.name
        return permName === 'super.admin'
      })

    if (isSuperAdmin) {
      return true
    }

    // Regular permission check
    return user.value.permissions.some(p => {
      const permName = typeof p === 'string' ? p : p.name
      return permName === permission
    })
  }

  const hasAnyPermission = (permissions: string[]): boolean => {
    if (!user.value) return false
    
    const isSuperAdmin =
      user.value.roles.some((r) => {
        const roleName = typeof r === 'string' ? r : r.name
        return roleName === 'super-admin'
      }) ||
      user.value.permissions.some((p) => {
        const permName = typeof p === 'string' ? p : p.name
        return permName === 'super.admin'
      })
    
    if (isSuperAdmin) {
      return true
    }
    
    // Regular permission check - user needs at least one of the specified permissions
    return permissions.some(permission => {
      return user.value?.permissions.some(p => {
        const permName = typeof p === 'string' ? p : p.name
        return permName === permission
      })
    })
  }

  const hasAllPermissions = (permissions: string[]): boolean => {
    if (!user.value) return false
    return permissions.every(permission => {
      return user.value?.permissions.some(p => {
        const permName = typeof p === 'string' ? p : p.name
        return permName === permission
      })
    })
  }

  const hasRole = (role: string): boolean => {
    if (!user.value) return false
    return user.value.roles.some(r => {
      const roleName = typeof r === 'string' ? r : r.name
      return roleName === role
    })
  }

  const hasAnyRole = (roles: string[]): boolean => {
    if (!user.value) return false
    return roles.some(role => {
      return user.value?.roles.some(r => {
        const roleName = typeof r === 'string' ? r : r.name
        return roleName === role
      })
    })
  }

  const startImpersonation = async (userUuid: string): Promise<void> => {
    if (!token.value) {
      throw new Error('Not authenticated')
    }

    isLoading.value = true
    try {
      const response = await apiService.post<AuthMeResponse>('/auth/impersonation/start', {
        user_uuid: userUuid,
      })
      applyAuthMePayload(response)
    } finally {
      isLoading.value = false
    }
  }

  const stopImpersonation = async (): Promise<void> => {
    if (!token.value) {
      throw new Error('Not authenticated')
    }

    isLoading.value = true
    try {
      const response = await apiService.post<AuthMeResponse>('/auth/impersonation/stop', {})
      applyAuthMePayload(response)
    } finally {
      isLoading.value = false
    }
  }

  const updateTimezone = async (timezone: string): Promise<void> => {
    if (!token.value) throw new Error('Not authenticated')
    
    try {
      await apiService.put('/personal/settings/timezone', { timezone })
      
      // Update user in store
      if (user.value) {
        user.value.timezone = timezone
        localStorage.setItem('auth_user', JSON.stringify(user.value))
      }
    } catch (error) {
      console.error('Failed to update timezone:', error)
      throw error
    }
  }

  const hasTimezone = computed(() => {
    return !!user.value?.timezone
  })

  /**
   * When true, App.vue keeps /settings/two-factor on the root RouterView (no AppLayoutWithSidebar)
   * even if the user already has 2FA in the store. Used only while the post-enable recovery modal is
   * open so refreshUser() cannot swap the shell and remount TwoFactorAuth (which would drop modal state).
   */
  const hideAppSidebarForTwoFactorSettings = ref(false)

  const setHideAppSidebarForTwoFactorSettings = (value: boolean) => {
    hideAppSidebarForTwoFactorSettings.value = value
  }

  // Initialize auth state on store creation
  initAuth()

  apiService.setHardLogoutCallback(clearAuth)

  return {
    // State
    user,
    token,
    impersonation,
    impersonationTransitionInProgress,
    isLoading,
    isAuthenticated,
    isImpersonating,
    hasTimezone,
    sessionRefreshDegraded,
    hideAppSidebarForTwoFactorSettings,
    
    // Actions
    initAuth,
    login,
    register,
    logout,
    clearAuth,
    refreshUser,
    startImpersonation,
    stopImpersonation,
    updateTimezone,
    setHideAppSidebarForTwoFactorSettings,
    /** Legacy hook used by TwoFactorVerifyView after successful verification */
    setUserDataRefreshedFromApi: (_refreshed: boolean) => {},
    
    // Helpers
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
  }
})

